import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useNavScroll } from '../../hooks/useScrollAnimation';
import { BUSINESS, NAV_LINKS } from '../../data/siteData';

export default function Navbar() {
  const scrolled = useNavScroll();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href.startsWith('/#')) {
      if (location.pathname !== '/') {
        window.location.href = href;
        return;
      }
      const id = href.replace('/#', '');
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          background: 'rgba(13,27,42,0.97)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          transition: 'box-shadow 0.3s',
          boxShadow: scrolled ? '0 4px 30px rgba(0,0,0,0.4)' : 'none',
        }}
      >
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 2rem', height: 68,
        }}>
          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none' }}>
            <div style={{
              width: 40, height: 40, background: 'var(--orange)', borderRadius: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem',
            }}>🔧</div>
            <div style={{ lineHeight: 1 }}>
              <span style={{
                display: 'block', fontFamily: '"Bebas Neue"', fontSize: '1.25rem',
                color: 'white', letterSpacing: '0.05em',
              }}>Brantford Auto</span>
              <span style={{
                fontFamily: '"Barlow Condensed"', fontSize: '0.7rem',
                color: 'var(--silver)', letterSpacing: '0.12em', textTransform: 'uppercase',
              }}>Wrecking & Salvage</span>
            </div>
          </Link>

          {/* Desktop links */}
          <ul style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', listStyle: 'none', margin: 0, padding: 0 }}
              className="desktop-nav">
            {NAV_LINKS.map(link => (
              <li key={link.label}>
                {link.href.startsWith('/#') ? (
                  <button
                    onClick={() => handleNavClick(link.href)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--silver-light)', fontSize: '0.85rem', fontWeight: 500,
                      fontFamily: 'Barlow, sans-serif',
                      letterSpacing: '0.04em', padding: '0.5rem 0.9rem', borderRadius: 4,
                      transition: 'color 0.2s, background 0.2s',
                    }}
                    onMouseEnter={e => { e.target.style.color = 'white'; e.target.style.background = 'rgba(255,255,255,0.08)'; }}
                    onMouseLeave={e => { e.target.style.color = 'var(--silver-light)'; e.target.style.background = 'none'; }}
                  >{link.label}</button>
                ) : (
                  <NavLink
                    to={link.href}
                    style={({ isActive }) => ({
                      textDecoration: 'none',
                      color: isActive ? 'white' : 'var(--silver-light)',
                      fontSize: '0.85rem', fontWeight: 500,
                      letterSpacing: '0.04em', padding: '0.5rem 0.9rem', borderRadius: 4,
                      background: isActive ? 'rgba(255,255,255,0.1)' : 'none',
                      transition: 'color 0.2s, background 0.2s',
                      display: 'block',
                    })}
                  >{link.label}</NavLink>
                )}
              </li>
            ))}
            <li>
              <a href={`tel:${BUSINESS.phoneRaw}`} style={{
                background: 'var(--orange)', color: 'white', textDecoration: 'none',
                fontFamily: '"Barlow Condensed"', fontSize: '0.9rem', fontWeight: 700,
                letterSpacing: '0.05em', padding: '0.5rem 1.1rem', borderRadius: 4,
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.target.style.background = 'var(--orange-light)'}
              onMouseLeave={e => e.target.style.background = 'var(--orange)'}
              >📞 {BUSINESS.phone}</a>
            </li>
          </ul>

          {/* Hamburger */}
          <button
            onClick={() => setMobileOpen(o => !o)}
            className="hamburger-btn"
            aria-label="Toggle menu"
            style={{
              display: 'none', flexDirection: 'column', gap: 5, cursor: 'pointer',
              background: 'none', border: 'none', padding: 4,
            }}
          >
            <span style={{ display: 'block', width: 24, height: 2, background: 'var(--silver-light)', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 24, height: 2, background: 'var(--silver-light)', borderRadius: 2 }} />
            <span style={{ display: 'block', width: 24, height: 2, background: 'var(--silver-light)', borderRadius: 2 }} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div style={{
          position: 'fixed', top: 68, left: 0, right: 0, zIndex: 99,
          background: 'var(--navy)', padding: '1rem',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          {NAV_LINKS.map(link => (
            link.href.startsWith('/#') ? (
              <button key={link.label}
                onClick={() => handleNavClick(link.href)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--silver-light)', fontSize: '1rem', fontWeight: 500,
                  fontFamily: 'Barlow, sans-serif',
                  padding: '0.75rem 1rem', borderRadius: 6,
                  transition: 'background 0.2s',
                }}
              >{link.label}</button>
            ) : (
              <Link key={link.label} to={link.href}
                onClick={() => setMobileOpen(false)}
                style={{
                  display: 'block', textDecoration: 'none', color: 'var(--silver-light)',
                  fontSize: '1rem', fontWeight: 500, padding: '0.75rem 1rem', borderRadius: 6,
                }}
              >{link.label}</Link>
            )
          ))}
          <a href={`tel:${BUSINESS.phoneRaw}`}
            style={{
              display: 'block', background: 'var(--orange)', color: 'white', textDecoration: 'none',
              fontFamily: '"Barlow Condensed"', fontSize: '1rem', fontWeight: 700,
              textAlign: 'center', padding: '0.85rem', borderRadius: 6, marginTop: '0.5rem',
              letterSpacing: '0.06em',
            }}
          >📞 Call {BUSINESS.phone}</a>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .hamburger-btn { display: flex !important; }
        }
      `}</style>
    </>
  );
}
